import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StepProgressBar from './components/StepProgressBar';
import PersonalDetailsForm from './components/PersonalDetailsForm';
import VehicleInfoForm from './components/VehicleInfoForm';
import DocumentUploadForm from './components/DocumentUploadForm';
import IDVerificationStep from './components/IDVerificationStep';
import BackgroundCheckStep from './components/BackgroundCheckStep';
import InsuranceStep from './components/InsuranceStep';
import TermsSubmitStep from './components/TermsSubmitStep';
import SuccessScreen from './components/SuccessScreen';
import { supabase } from '../../lib/supabase';

const TOTAL_STEPS = 7;

// Staggered skeleton for Driver Onboarding initial load
const DriverOnboardingSkeleton = () => (
  <div className="min-h-screen" style={{ background: '#f8fafc' }}>
    {/* Skeleton Header */}
    <div className="sticky top-0 z-40 shadow-md" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 100%)' }}>
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#ffd700]/30 animate-pulse" style={{ animationDelay: '0ms' }} />
            <div className="w-16 h-3.5 rounded-full bg-white/20 animate-pulse hidden sm:block" style={{ animationDelay: '60ms' }} />
          </div>
          <div className="text-center space-y-1.5">
            <div className="h-3.5 w-32 rounded-full bg-white/20 animate-pulse mx-auto" style={{ animationDelay: '120ms' }} />
            <div className="h-2.5 w-20 rounded-full bg-white/10 animate-pulse mx-auto" style={{ animationDelay: '180ms' }} />
          </div>
          <div className="w-6 h-6 rounded bg-white/10 animate-pulse" style={{ animationDelay: '240ms' }} />
        </div>
      </div>
    </div>

    {/* Skeleton Hero Banner */}
    <div className="bg-gradient-to-r from-[#0a1628] to-[#1a365d] px-4 py-6">
      <div className="max-w-2xl mx-auto flex items-center gap-4">
        <div className="flex-1 space-y-2">
          <div className="h-5 w-48 rounded-full bg-white/15 animate-pulse" style={{ animationDelay: '150ms' }} />
          <div className="h-3 w-64 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: '210ms' }} />
          <div className="flex gap-3 mt-3">
            {[270, 330, 390]?.map(delay => (
              <div key={delay} className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-full bg-[#ffd700]/30 animate-pulse" style={{ animationDelay: `${delay}ms` }} />
                <div className="h-2.5 w-14 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: `${delay + 30}ms` }} />
              </div>
            ))}
          </div>
        </div>
        <div className="w-20 h-20 rounded-2xl bg-white/10 animate-pulse flex-shrink-0" style={{ animationDelay: '300ms' }} />
      </div>
    </div>

    {/* Skeleton Step Progress Bar */}
    <div className="max-w-2xl mx-auto px-4 py-4">
      <div className="flex items-center justify-between mb-2">
        <div className="h-3 w-20 rounded-full bg-[#0a1628]/10 animate-pulse" style={{ animationDelay: '200ms' }} />
        <div className="h-3 w-16 rounded-full bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: '260ms' }} />
      </div>
      <div className="flex items-center gap-1">
        {Array.from({ length: TOTAL_STEPS })?.map((_, i) => (
          <React.Fragment key={i}>
            <div
              className={`w-7 h-7 rounded-full animate-pulse flex-shrink-0 ${i === 0 ? 'bg-[#ffd700]/50' : 'bg-[#0a1628]/10'}`}
              style={{ animationDelay: `${250 + i * 50}ms` }}
            />
            {i < TOTAL_STEPS - 1 && (
              <div className="flex-1 h-1 rounded-full bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: `${280 + i * 50}ms` }} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>

    {/* Skeleton Form Card */}
    <div className="max-w-2xl mx-auto px-4 pb-12">
      <div className="bg-white rounded-2xl shadow-sm border border-[#0a1628]/8 p-6 space-y-5">
        {/* Form title */}
        <div className="space-y-2 pb-2 border-b border-[#0a1628]/8">
          <div className="h-5 w-40 rounded-full bg-[#0a1628]/12 animate-pulse" style={{ animationDelay: '300ms' }} />
          <div className="h-3 w-64 rounded-full bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: '360ms' }} />
        </div>

        {/* Form fields — Personal Details layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[400, 460, 520, 580]?.map(delay => (
            <div key={delay} className="space-y-1.5">
              <div className="h-2.5 w-20 rounded-full bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: `${delay}ms` }} />
              <div className="h-11 rounded-xl bg-[#0a1628]/6 animate-pulse" style={{ animationDelay: `${delay + 30}ms` }} />
            </div>
          ))}
        </div>

        {/* Phone field full width */}
        <div className="space-y-1.5">
          <div className="h-2.5 w-24 rounded-full bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: '640ms' }} />
          <div className="h-11 rounded-xl bg-[#0a1628]/6 animate-pulse" style={{ animationDelay: '670ms' }} />
        </div>

        {/* Email field full width */}
        <div className="space-y-1.5">
          <div className="h-2.5 w-16 rounded-full bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: '700ms' }} />
          <div className="h-11 rounded-xl bg-[#0a1628]/6 animate-pulse" style={{ animationDelay: '730ms' }} />
        </div>

        {/* Info box */}
        <div className="h-14 rounded-xl bg-blue-50 animate-pulse" style={{ animationDelay: '760ms' }} />

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          <div className="flex-1 h-12 rounded-xl bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: '800ms' }} />
          <div className="flex-1 h-12 rounded-xl bg-[#ffd700]/30 animate-pulse" style={{ animationDelay: '860ms' }} />
        </div>
      </div>
    </div>
  </div>
);

const DriverOnboardingScreen = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    // Personal
    fullName: '',
    phone: '',
    email: '',
    password: '',
    nationalId: '',
    // Vehicle
    vehicleType: '',
    plateNumber: '',
    vehicleModel: '',
    vehicleYear: '',
    // Documents
    nationalIdDoc: null,
    drivingLicense: null,
    vehicleInsurance: null,
    profileSelfie: null,
    // Consent & Insurance
    backgroundCheckConsent: false,
    insurancePlan: 'premium',
    // Terms
    termsAccepted: false,
    privacyAccepted: false,
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateDocField = (docKey, value) => {
    setFormData((prev) => ({ ...prev, [docKey]: value }));
  };

  const goNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // STEP 1: Create Supabase Auth user with driver role metadata
      const { data: authData, error: signUpError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName,
            role: 'driver',
          },
        },
      });

      if (signUpError) throw signUpError;

      const userId = authData?.user?.id;
      if (!userId) throw new Error('Account creation failed. Please try again.');

      // STEP 2: Upsert user_profiles with driver role
      // The handle_new_user trigger may have already created a basic profile;
      // upsert ensures name, phone, and role are correctly set.
      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.upsert({
          id: userId,
          email: formData?.email,
          full_name: formData?.fullName,
          phone: formData?.phone,
          role: 'driver',
        }, { onConflict: 'id' });

      if (profileError) throw profileError;

      // STEP 3: Insert driver_profiles record linked via user_id
      const { error: driverProfileError } = await supabase
        ?.from('driver_profiles')
        ?.upsert({
          user_id: userId,
          vehicle_type: formData?.vehicleType || 'boda',
          vehicle_name: formData?.vehicleModel || null,
          vehicle_year: formData?.vehicleYear || null,
          license_plate: formData?.plateNumber || null,
          status: 'offline',
          is_verified: false,
          updated_at: new Date()?.toISOString(),
        }, { onConflict: 'user_id' });

      if (driverProfileError) throw driverProfileError;

      // STEP 4: Save onboarding application record (linked to the new user)
      const { data: application, error } = await supabase
        ?.from('driver_onboarding_applications')
        ?.insert({
          full_name: formData?.fullName,
          phone: formData?.phone,
          email: formData?.email,
          national_id: formData?.nationalId,
          vehicle_type: formData?.vehicleType,
          plate_number: formData?.plateNumber,
          vehicle_model: formData?.vehicleModel,
          vehicle_year: formData?.vehicleYear,
          background_check_consent: formData?.backgroundCheckConsent,
          insurance_enrolled: true,
          terms_accepted: formData?.termsAccepted,
          status: 'submitted',
          submitted_at: new Date()?.toISOString(),
          id_verification_status: 'verified',
          user_id: userId,
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // STEP 5: Save document records
      const docTypes = [
        { key: 'nationalIdDoc', type: 'national_id' },
        { key: 'drivingLicense', type: 'driving_license' },
        { key: 'vehicleInsurance', type: 'vehicle_insurance' },
        { key: 'profileSelfie', type: 'profile_selfie' },
      ];

      const docInserts = docTypes?.filter((d) => formData?.[d?.key])?.map((d) => ({
        application_id: application?.id,
        document_type: d?.type,
        file_path: formData?.[d?.key]?.filePath,
        file_name: formData?.[d?.key]?.fileName,
        file_size: formData?.[d?.key]?.fileSize,
        upload_status: 'uploaded',
      }));

      if (docInserts?.length > 0) {
        await supabase?.from('driver_documents')?.insert(docInserts);
      }

      setApplicationId(application?.id);
      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Submission error:', err);
      setSubmitError(err?.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalDetailsForm
            data={formData}
            onChange={updateField}
            onNext={goNext}
          />
        );
      case 2:
        return (
          <VehicleInfoForm
            data={formData}
            onChange={updateField}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 3:
        return (
          <DocumentUploadForm
            data={{
              nationalId: formData?.nationalIdDoc,
              drivingLicense: formData?.drivingLicense,
              vehicleInsurance: formData?.vehicleInsurance,
              profileSelfie: formData?.profileSelfie,
            }}
            onChange={(key, value) => {
              const keyMap = {
                nationalId: 'nationalIdDoc',
                drivingLicense: 'drivingLicense',
                vehicleInsurance: 'vehicleInsurance',
                profileSelfie: 'profileSelfie',
              };
              updateDocField(keyMap?.[key] || key, value);
            }}
            onNext={goNext}
            onBack={goBack}
            applicationId={applicationId}
          />
        );
      case 4:
        return <IDVerificationStep onNext={goNext} onBack={goBack} />;
      case 5:
        return (
          <BackgroundCheckStep
            data={formData}
            onChange={updateField}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 6:
        return (
          <InsuranceStep
            data={formData}
            onChange={updateField}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 7:
        return (
          <TermsSubmitStep
            data={formData}
            onChange={updateField}
            onSubmit={handleSubmit}
            onBack={goBack}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  // Show skeleton on initial load
  React.useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 900);
    return () => clearTimeout(timer);
  }, []);

  if (pageLoading) {
    return <DriverOnboardingSkeleton />;
  }

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-40 shadow-md"
        style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 100%)' }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 group"
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs shadow-md group-hover:scale-110 transition-transform duration-200"
                style={{ background: 'linear-gradient(135deg, #ffd700, #f59e0b)', color: '#0a1628' }}
              >
                OG
              </div>
              <span className="font-bold text-white text-sm hidden sm:block">OnTheGo</span>
            </button>

            <div className="text-center">
              <p className="text-white font-bold text-sm">Driver Registration</p>
              {!isSuccess && (
                <p className="text-xs" style={{ color: 'rgba(255,215,0,0.7)' }}>
                  Step {currentStep} of {TOTAL_STEPS}
                </p>
              )}
            </div>

            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
      {/* Hero Banner (Step 1 only) */}
      {currentStep === 1 && !isSuccess && (
        <div
          className="py-8 px-4"
          style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 100%)' }}
        >
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-4xl mb-3">🏍️</div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Become an OnTheGo Driver
            </h1>
            <p className="text-sm" style={{ color: 'rgba(255,215,0,0.8)' }}>
              Earn on your schedule · Full insurance coverage · Weekly payouts
            </p>
            <div className="flex items-center justify-center gap-6 mt-4">
              {[
                { icon: '💰', label: 'KES 80K+/mo' },
                { icon: '⏰', label: 'Flexible Hours' },
                { icon: '🛡️', label: 'Full Insurance' },
              ]?.map((item) => (
                <div key={item?.label} className="text-center">
                  <div className="text-lg">{item?.icon}</div>
                  <p className="text-xs font-semibold mt-1" style={{ color: 'rgba(255,215,0,0.9)' }}>
                    {item?.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Progress Bar */}
      {!isSuccess && (
        <div className="max-w-2xl mx-auto px-4">
          <StepProgressBar currentStep={currentStep} />
        </div>
      )}
      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 pb-12">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
          {isSuccess ? (
            <SuccessScreen applicationData={formData} />
          ) : (
            <>
              {renderStep()}
              {submitError && (
                <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200">
                  <p className="text-red-600 text-sm">{submitError}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverOnboardingScreen;
